import React, { memo } from 'react';
import { StyleSheet } from 'react-native';

import { LI, UL } from '@expo/html-elements';
import { theme } from '@garnish/constants';
import { LoadingPlaceholder, useResponsive } from '@garnish/core';

import { useA11ySelector } from '@sg/a11y-helpers';

import { type AddressWithoutIds, type LocationAddress } from '../../../locations.types';
import { LocationSearchAddress } from '../LocationSearchAddress';
import { LocationSearchScrollView } from '../LocationSearchScrollView';

export const LocationSearchPredictionAddressesList = memo(
  (props: LocationSearchPredictionAddressesListProps) => {
    const { addresses, onPress, isLoading = false } = props;

    const { match } = useResponsive();
    const a11ySelector = useA11ySelector('locations.delivery.addresses-container'); // prettier-ignore

    // ─── Styles ────────────────────────────────────────────────────────

    const containerStyles = match([styles.containerXS, undefined]);
    const contentContainerStyles = [
      styles.contentContainer,
      isLoading ? styles.loadingContainer : undefined,
      isLoading ? match([styles.loadingContainerXS, styles.loadingContainerSM]) : undefined,
    ];

    // ─────────────────────────────────────────────────────────────────────

    return (
      <LocationSearchScrollView
        style={containerStyles}
        contentContainerStyle={contentContainerStyles}
        a11ySelector={a11ySelector}
      >
        {isLoading ? (
          <LoadingPlaceholder rows={5} rowHeight={44} palette="glass" />
        ) : (
          <UL>
            {addresses.map((address) => {
              return (
                <LI key={address.id}>
                  <LocationSearchAddress
                    variation="prediction-address"
                    address={address}
                    onPress={onPress}
                  />
                </LI>
              );
            })}
          </UL>
        )}
      </LocationSearchScrollView>
    );
  },
);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    ...StyleSheet.absoluteFill,
    zIndex: 1,
  },
  contentContainer: {
    padding: theme.spacing['2'],
  },
  loadingContainer: {
    minHeight: 300,
    paddingHorizontal: theme.spacing['3'],
  },
  loadingContainerXS: {
    paddingTop: theme.spacing['7'],
  },
  loadingContainerSM: {
    paddingTop: theme.spacing['3'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchPredictionAddressesListProps = {
  addresses: LocationAddress[] | readonly LocationAddress[];
  onPress: (predictedAddress: AddressWithoutIds) => void;
  isLoading?: boolean;
};
